import { useEffect, useState } from 'react'
import { useIsAuthenticated, useMsal } from '@azure/msal-react'
import { deleteSession, LoginBadge, msalAuthSettings, updateSession } from '@dfds-pax/login'
import { getUrlLocale } from '@dfds-pax/common'
import { AuthenticationResult, IPublicClientApplication } from '@azure/msal-browser'
import { Content, getContent } from './content'

const Login: React.FC = () => {
  const { instance, inProgress } = useMsal()
  let isLoggedIn = useIsAuthenticated()
  const [credentials, setCredentials] = useState<string>('-')
  const [content, setContent] = useState<Content>({
    MENU_ONE_TITLE: 'Personal details',
    MENU_TWO_TITLE: 'My bookings',
    MENU_THREE_TITLE: 'Consent and privacy',
    MY_PROFILE: 'My profile',
    LOGIN: 'Log in',
    FIRST_TIME_HERE: 'First time here?',
    CREATE_NEW_ACCOUNT: 'Create a new account',
    LOGOUT: 'Log out',
  })

  const locale = getUrlLocale() || 'en'

  useEffect(() => {
    ;(async () => {
      const _content = await getContent()
      setContent(_content)
      instance.handleRedirectPromise().then(async (tokenResponse) => {
        if (tokenResponse) {
          instance.setActiveAccount(tokenResponse.account)
          await updateSession(tokenResponse.accessToken)
        }
      })
    })()
  }, [])

  useEffect(() => {
    ;(async () => {
      const authenticationResult: AuthenticationResult | void = await instance
        .acquireTokenSilent({ scopes: msalAuthSettings.scopes })
        .catch(async () => {
          isLoggedIn = false
        })

      if (!authenticationResult?.accessToken) isLoggedIn = false

      if (authenticationResult?.accessToken) {
        await updateSession(authenticationResult.accessToken)
        isLoggedIn = true
      }

      const _credentials = getFirstLetter(instance)
      setCredentials(_credentials)
    })()
  }, [instance, inProgress])

  return (
    <LoginBadge
      content={content}
      locale={locale}
      isLoggedIn={isLoggedIn}
      credentials={credentials}
      onCreateAccountClicked={() => {
        instance.loginRedirect({
          scopes: msalAuthSettings.scopes,
          redirectUri: `${window.location.origin}/redirect`,
          extraQueryParameters: { flowState: 'signup', ui_locales: locale },
        })
      }}
      onLoginClicked={() => {
        instance.loginRedirect({
          scopes: msalAuthSettings.scopes,
          extraQueryParameters: { ui_locales: locale ?? 'en' },
        })
      }}
      onLogoutClicked={async () => {
        await deleteSession()
        await instance.logout({
          postLogoutRedirectUri: `/${locale}`,
        })
      }}
    />
  )
}

export default Login

const getFirstLetter = (instance: IPublicClientApplication) => {
  const firstName = instance.getActiveAccount()?.idTokenClaims?.given_name as string
  const email = instance.getActiveAccount()?.idTokenClaims?.email as string
  if (firstName) return firstName.slice(0, 1).toUpperCase()
  if (email) return email.slice(0, 1).toUpperCase()
  return '-'
}
